import dayjs from 'dayjs';

export const ADVANCED_SEARCH_DEFAULT_PAGE_SIZE = 20;

const toTrimmed = (value) => {
	if (typeof value !== 'string') {
		return value ?? '';
	}
	return value.trim();
};

const toNullableTrimmed = (value) => {
	if (typeof value !== 'string') {
		return value ?? null;
	}
	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : null;
};

const formatDate = (value, useEndOfDay) => {
	if (!value) {
		return null;
	}
	if (typeof value.format === 'function') {
		return value.format(useEndOfDay ? 'YYYY-MM-DDT23:59:59' : 'YYYY-MM-DDT00:00:00');
	}
	const parsed = dayjs(value);
	if (!parsed.isValid()) {
		return null;
	}
	return parsed.format(useEndOfDay ? 'YYYY-MM-DDT23:59:59' : 'YYYY-MM-DDT00:00:00');
};

const resolveStatusList = (source = {}) => {
	if (Array.isArray(source.orderStatus) && source.orderStatus.length > 0) {
		return source.orderStatus;
	}
	if (Array.isArray(source.selectedStatus) && source.selectedStatus.length > 0) {
		return source.selectedStatus;
	}
	return [];
};

export const buildAdvancedSearchRequest = (source = {}) => {
	return {
		orderId: toNullableTrimmed(source.orderId),
		orderNumber: toNullableTrimmed(source.orderNumber),
		customerName: toNullableTrimmed(source.customerName),
		buyerName: toNullableTrimmed(source.buyerName),
		startDate: formatDate(source.startDate, false),
		endDate: formatDate(source.endDate, true),
		orderStatus: resolveStatusList(source).length > 0
			? resolveStatusList(source).join(',')
			: null,
		productSku: null,
	};
};

export const isAdvancedSearchRequestEmpty = (request = {}) => {
	return !request.orderId &&
		!request.orderNumber &&
		!request.customerName &&
		!request.buyerName &&
		!request.startDate &&
		!request.endDate &&
		!request.orderStatus &&
		!request.productSku;
};

export const normalizeAdvancedSearchResponse = (raw) => {
	if (Array.isArray(raw)) {
		return raw;
	}
	if (raw && typeof raw === 'object') {
		if (Array.isArray(raw.orders)) return raw.orders;
		if (Array.isArray(raw.items)) return raw.items;
		if (Array.isArray(raw.content)) return raw.content;
		if (Array.isArray(raw.results)) return raw.results;
		if (Array.isArray(raw.data)) return raw.data;
		if (Array.isArray(raw.records)) return raw.records;
		if (Array.isArray(raw.payload)) return raw.payload;
	}
	return [];
};

const buildName = (first, last) => {
	const parts = [];
	if (first && typeof first === 'string') parts.push(first.trim());
	if (last && typeof last === 'string') parts.push(last.trim());
	return parts.filter(Boolean).join(' ').trim();
};

const uniqueCandidates = (values = []) => {
	const set = new Set();
	values.forEach((value) => {
		if (typeof value === 'string') {
			const trimmed = value.trim();
			if (trimmed.length > 0) {
				set.add(trimmed);
			}
		}
	});
	return Array.from(set.values());
};

const collectBuyerCandidates = (order = {}) => {
	return uniqueCandidates([
		order.buyerName,
		buildName(order.buyerFirstName, order.buyerLastName),
		buildName(order.buyerLastName, order.buyerFirstName),
		order.customerName,
		buildName(order.customerFirstName, order.customerLastName),
	]);
};

const collectCustomerCandidates = (order = {}) => {
	return uniqueCandidates([
		order.customerName,
		buildName(order.customerFirstName, order.customerLastName),
		buildName(order.customerLastName, order.customerFirstName),
		order.buyerName,
		buildName(order.buyerFirstName, order.buyerLastName),
	]);
};

const tokensFromQuery = (query) => {
	if (typeof query !== 'string') {
		return [];
	}
	return query
		.trim()
		.toLowerCase()
		.split(/\s+/)
		.filter(Boolean);
};

const matchesTokens = (candidates, tokens) => {
	if (tokens.length === 0) {
		return true;
	}
	if (!Array.isArray(candidates) || candidates.length === 0) {
		return false;
	}
	return candidates.some((candidate) => {
		const lower = candidate.toLowerCase();
		return tokens.every((token) => lower.includes(token));
	});
};

export const filterAdvancedResults = (results, source = {}) => {
	const normalized = Array.isArray(results) ? results : [];
	const buyerTokens = tokensFromQuery(source.buyerName);
	const customerTokens = tokensFromQuery(source.customerName);

	if (buyerTokens.length === 0 && customerTokens.length === 0) {
		return normalized;
	}

	return normalized.filter((order) => {
		const buyerMatch = matchesTokens(collectBuyerCandidates(order), buyerTokens);
		const customerMatch = matchesTokens(collectCustomerCandidates(order), customerTokens);
		if (buyerTokens.length > 0 && customerTokens.length > 0) {
			return buyerMatch && customerMatch;
		}
		if (buyerTokens.length > 0) {
			return buyerMatch;
		}
		return customerMatch;
	});
};

export const sanitizeAdvancedSearchInputs = (source = {}) => {
	return {
		...source,
		orderId: toTrimmed(source.orderId),
		orderNumber: toTrimmed(source.orderNumber),
		customerName: toTrimmed(source.customerName),
		buyerName: toTrimmed(source.buyerName),
		orderStatus: Array.isArray(source.orderStatus) ? source.orderStatus : [],
	};
};